// @ts-check
import { createLifetime } from './lifetime.js';
import { watchPads } from './gamepad.js';
/** @typedef {import('../game/types.js').Action} Action */
/** @type {ReadonlyMap<string,Action>} */
export const KEY_ACTIONS = new Map([
  ['ArrowLeft', 'left'], ['KeyA', 'left'], ['ArrowRight', 'right'], ['KeyD', 'right'], ['KeyF', 'pulse'], ['KeyC', 'pulse'],
  ['Space', 'jump'], ['ArrowUp', 'jump'], ['KeyW', 'jump'], ['ShiftLeft', 'sprint'],
  ['ShiftRight', 'sprint'], ['KeyX', 'sprint'], ['ArrowDown', 'down'], ['KeyS', 'down'],
]);
/** @param {string} value @returns {value is Action} */
export function isAction(value) { return ['left', 'right', 'jump', 'sprint', 'down', 'pulse'].includes(value); }

const NAV_REPEAT_DELAY = 400;
const NAV_REPEAT_EVERY = 150;

/** Translate device events into held actions and one-shot presses. Owns and releases every listener.
 * Gamepads have no button events, so connected pads are polled once per display frame.
 * @param {Window} target @param {HTMLButtonElement[]} buttons
 * @param {{enabled:()=>boolean,changed:()=>void,pressed:(action:Action)=>void,restart:()=>void,now:()=>number,control?:(control:import('./gamepad.js').PadControl)=>void}} callbacks
 * @param {{gamepads?:()=>ArrayLike<Gamepad|null>,request?:(callback:FrameRequestCallback)=>number,cancel?:(id:number)=>void}} [devices]
 */
export function createInput(target, buttons, callbacks, devices = {}) {
  const life = createLifetime();
  /** @type {Set<string>} */ const keys = new Set();
  /** @type {Map<number,Action>} */ const pointers = new Map();
  /** @type {Map<number,number>} */ const pointerTimes = new Map();
  /** @type {Map<Action,number>} */ const taps = new Map();
  /** @type {Set<Action>} */ const padHeld = new Set();
  /** @param {Action} action */
  function held(action) {
    return padHeld.has(action) || [...pointers.values()].includes(action) || taps.has(action) || [...keys].some((key) => KEY_ACTIONS.get(key) === action);
  }
  let navNext = 0;
  /** @param {import('./gamepad.js').PadState|null} state @param {import('./gamepad.js').PadState} previous */
  function readPadFrame(state, previous) {
    if (!state) { if (padHeld.size) { padHeld.clear(); callbacks.changed(); } return; }
    const enabled = callbacks.enabled();
    const before = [...padHeld].sort().join();
    for (const action of state.actions) {
      // Compare against the raw previous reading: a button held through Start, a menu, or a
      // clear() resumes as a hold and never replays its press.
      if (enabled && !previous.actions.has(action) && !held(action)) callbacks.pressed(action);
    }
    padHeld.clear();
    if (enabled) for (const action of state.actions) padHeld.add(action);
    const control = callbacks.control;
    if (control) {
      if (state.start && !previous.start) control('start');
      if (state.select && !previous.select) control('select');
      if (!enabled && state.confirm && !previous.confirm) control('confirm');
      const now = callbacks.now();
      // A direction held into a pause, uplink, or result is still running QP, not choosing a menu item.
      // Only a fresh press walks focus once play stops.
      if (enabled) navNext = Infinity;
      if (!enabled && state.nav && (state.nav !== previous.nav || now >= navNext)) {
        navNext = now + (state.nav !== previous.nav ? NAV_REPEAT_DELAY : NAV_REPEAT_EVERY);
        control(state.nav < 0 ? 'previous' : 'next');
      }
    }
    if ([...padHeld].sort().join() !== before) callbacks.changed();
  }
  if (devices.gamepads && devices.request) {
    const pads = watchPads(target, { gamepads: devices.gamepads, request: devices.request, cancel: devices.cancel ?? (() => {}) }, readPadFrame);
    life.add(() => pads.dispose());
  }
  /** @param {Event} event */
  const editable = (event) => event.target instanceof HTMLElement && event.target.closest("input, textarea, select, [contenteditable='true']");
  life.listen(target, 'keydown', (raw) => {
    const event = /** @type {KeyboardEvent} */ (raw);
    if (event.altKey || event.ctrlKey || event.metaKey || editable(event)) return;
    if (event.code === 'KeyR') { if (!event.repeat) callbacks.restart(); return; }
    if (!callbacks.enabled()) return;
    const action = KEY_ACTIONS.get(event.code);
    if (!action || (event.code === 'Space' && event.target instanceof HTMLButtonElement)) return;
    event.preventDefault();
    const already = held(action); keys.add(event.code);
    if (!already && !event.repeat) callbacks.pressed(action);
    callbacks.changed();
  });
  life.listen(target, 'keyup', (raw) => {
    const event = /** @type {KeyboardEvent} */ (raw);
    keys.delete(event.code); if (KEY_ACTIONS.has(event.code)) callbacks.changed();
  });
  for (const button of buttons) {
    const action = button.dataset.action ?? '';
    if (!isAction(action)) throw new Error(`Unknown input action: ${action}`);
    life.listen(button, 'keydown', (raw) => {
      const event = /** @type {KeyboardEvent} */ (raw);
      if (event.repeat && ['Enter', 'Space'].includes(event.code)) event.preventDefault();
    });
    life.listen(button, 'pointerdown', (raw) => {
      const event = /** @type {PointerEvent} */ (raw);
      if (event.button !== 0 || !callbacks.enabled()) return;
      event.preventDefault(); button.setPointerCapture(event.pointerId);
      const already = held(action); pointers.set(event.pointerId, action);
      pointerTimes.set(event.pointerId, callbacks.now());
      if (!already) callbacks.pressed(action);
      callbacks.changed();
    });
    const release = (/** @type {Event} */ raw) => {
      const id = (/** @type {PointerEvent} */ (raw)).pointerId;
      const began = pointerTimes.get(id);
      // Short arrow taps must remain visible even between two display frames.
      if (raw.type === 'pointerup' && began !== undefined && (action === 'left' || action === 'right') && callbacks.enabled() && callbacks.now() - began < 140) taps.set(action, began + 140);
      pointers.delete(id); pointerTimes.delete(id); callbacks.changed();
    };
    for (const event of ['pointerup', 'pointercancel', 'lostpointercapture']) life.listen(button, event, release);
    life.listen(button, 'click', (raw) => {
      if ((/** @type {MouseEvent} */ (raw)).detail !== 0 || !callbacks.enabled()) return;
      if (!held(action)) callbacks.pressed(action);
      if (action !== 'jump' && action !== 'pulse') taps.set(action, callbacks.now() + (action === 'sprint' ? 1500 : action === 'down' ? 450 : 140));
      callbacks.changed();
    });
    life.listen(button, 'contextmenu', (event) => event.preventDefault());
  }
  function clear() { keys.clear(); pointers.clear(); pointerTimes.clear(); taps.clear(); padHeld.clear(); }
  return {
    held,
    sample() {
      for (const [action, until] of taps) if (callbacks.now() >= until) taps.delete(action);
      return { direction: Number(held('right')) - Number(held('left')), sprint: held('sprint'), down: held('down') };
    },
    clear,
    get hasTimedActions() { return taps.size > 0; },
    dispose() { clear(); life.dispose(); },
  };
}
